import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, IconButton, Popover, PopoverProps, Fade } from '@material-ui/core'
import { CirclePicker } from 'react-color'
import clsx from 'clsx'

import { useAppSelector, useAppDispatch } from 'planner-upgrades/redux/hooks'
import {
  optimizeTour,
  reverseTour,
  saveTourColor,
  setHiddenTourIds,
  setSelectedTourId,
} from 'actions/tours'
import { colours, contrastColor } from 'common/utils/Color'
import { areToursLocked } from 'selectors/combined'
import { getHiddenTourIds, getSelectedTourId } from 'selectors/tour'

import { ShipmentTour } from 'common/models/typings/Tour'

import Icon from 'common/components/base/Icon'
import Typography from 'common/components/base/Typography'
import Button from 'common/components/base/Button'

import useStyles from './useStyles'
import { Tooltip } from '../../../base/.raw'

const anchorOrigin: PopoverProps['anchorOrigin'] = {
  vertical: 'bottom',
  horizontal: 'right',
}

const transformOrigin: PopoverProps['transformOrigin'] = {
  vertical: 'top',
  horizontal: 'right',
}

export interface ActionsProps {
  id: ShipmentTour['id']
  color: ShipmentTour['color']
  hasBermudaStop: ShipmentTour['hasBermudaStop']
  isSelected: boolean
}

/**
 *
 * @param {string | number} id Shipment tour id
 * @param {string} color Shipment tour color
 * @param {boolean} hasBermudaStop Shipment tour if has bermuda stop
 * @returns
 */
const Actions: React.FC<ActionsProps> = ({
  id,
  color,
  hasBermudaStop,
  isSelected,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const classes = useStyles()

  const locked = useAppSelector(areToursLocked)
  const hiddenTourIds: ShipmentTour['id'][] = useAppSelector(getHiddenTourIds)
  const selectedTourId: ShipmentTour['id'] | null =
    useAppSelector(getSelectedTourId)

  const [isOptimizing, setIsOptimizing] = React.useState(false)

  const [colorPickerRef, setColorPickerRef] =
    React.useState<HTMLElement | null>(null)

  const isOptimizeLocked = React.useMemo(
    () => locked || hasBermudaStop,
    [locked, hasBermudaStop],
  )

  const isTourHidden = React.useMemo(
    () => hiddenTourIds.findIndex((hdt) => String(hdt) === String(id)) !== -1,
    [hiddenTourIds, id],
  )

  const isTourSelected = React.useMemo(
    () => String(id) === String(selectedTourId),
    [id, selectedTourId],
  )

  const isShowTourLocked = React.useMemo(
    () => locked || isSelected,
    [locked, isSelected],
  )

  const handleOptimizeTour = async () => {
    setIsOptimizing(true)
    await dispatch(optimizeTour(id))
    setIsOptimizing(false)
  }

  const handleReverseTour = () => {
    dispatch(reverseTour(id))
  }

  const handleToggleColorPicker = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setColorPickerRef((prev) => (prev === null ? event.currentTarget : null))
  }

  const handleHideTour = () => {
    dispatch(setHiddenTourIds(id))
  }

  const handleChangeColor = (selectedColor: string) => {
    if (color !== selectedColor) {
      dispatch(saveTourColor(id, selectedColor))
    }
  }

  const handleShowTour = () => {
    console.log('handleShowTour', id)
    dispatch(setSelectedTourId(id))
  }

  return (
    <Box className={classes.root}>
      <Tooltip text={t('OPTIMISE_TOUR')} placement="bottom">
        <Box component="span">
          <IconButton
            className={classes.button}
            disabled={isOptimizeLocked}
            onClick={handleOptimizeTour}
          >
            <Icon
              name="retry"
              className={clsx(classes.icon, isOptimizing && 'animate-spin')}
            />
          </IconButton>
        </Box>
      </Tooltip>

      <Tooltip text={t('REVERSE_TOUR')} placement="bottom">
        <Box component="span">
          <IconButton
            className={classes.button}
            disabled={locked}
            onClick={handleReverseTour}
          >
            <Icon name="swap" className={classes.icon} />
          </IconButton>
        </Box>
      </Tooltip>

      <Tooltip text={t('COLOR_TOUR')} placement="bottom">
        <Box component="span">
          <IconButton
            className={classes.button}
            disabled={locked}
            onClick={handleToggleColorPicker}
            style={{ backgroundColor: color }}
          >
            <Icon
              name="palette"
              fill={contrastColor(color)}
              className={classes.icon}
            />
          </IconButton>
        </Box>
      </Tooltip>
      <Popover
        open={Boolean(colorPickerRef)}
        anchorEl={colorPickerRef}
        onClose={handleToggleColorPicker}
        classes={{ paper: classes.colorPickerContainer }}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <Box marginBottom={1}>
          <Typography>{t('CHOOSE_TOUR_COLOUR')}</Typography>
        </Box>
        <CirclePicker
          width="100%"
          circleSpacing={12}
          circleSize={32}
          colors={colours}
          onChangeComplete={(clr) => handleChangeColor(clr.hex)}
          color={color}
        />
      </Popover>
      <Tooltip
        text={t(!isTourHidden ? 'HIDE_TOUR_TOOLTIP' : 'UNHIDE_TOUR_TOOLTIP')}
        placement="bottom"
      >
        <Box component="span">
          <IconButton
            className={classes.button}
            disabled={isShowTourLocked}
            onClick={handleHideTour}
          >
            {!isTourHidden ? <Icon name="view" /> : <Icon name="hide" />}
          </IconButton>
        </Box>
      </Tooltip>
      <Tooltip
        text={t(!isTourSelected ? 'SHOW_FULL_TOUR' : 'HIDE_TOUR')}
        placement="bottom"
      >
        <button
          className="flex items-center justify-center ml-[-2px]"
          onClick={handleShowTour}
        >
          <Icon
            name="caret-up"
            className={clsx(
              'rotate-180 transition-transform duration-300',
              !isTourSelected && 'rotate-90',
            )}
          />
        </button>
      </Tooltip>
    </Box>
  )
}

export default React.memo(Actions)
